import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';

export type UiMessageSeverity = 'success' | 'info' | 'warn' | 'error' | 'secondary' | 'contrast' | string;
export type UiMessageSize = 'small' | 'large' | undefined;
export type UiMessageVariant = 'outlined' | 'text' | 'simple' | undefined;

@Component({
    selector: 'app-message',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './app-message.component.html',
    styleUrls: ['./app-message.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class AppMessageComponent implements OnInit, OnChanges, OnDestroy {
    /**
     * Severity level of the message.
     * @defaultValue 'info'
     */
    @Input() severity: UiMessageSeverity | null | undefined = 'info';

    /**
     * Text content.
     */
    @Input() text: string | undefined;

    /**
     * Whether displaying messages would be escaped or not.
     * @defaultValue true
     */
    @Input() escape = true;

    /**
     * Inline style of the root message container.
     */
    @Input() style: { [klass: string]: any } | string | null | undefined;

    /**
     * Style class of the message container.
     */
    @Input() styleClass: string | undefined;

    /**
     * Whether the message can be closed manually using the close icon.
     * @defaultValue false
     */
    @Input() closable = false;

    /**
     * Icon to display in the message (PrimeIcons class string, e.g. "pi pi-info-circle").
     * If undefined, no icon is shown.
     */
    @Input() icon: string | undefined;

    /**
     * Icon to display in the message close button.
     */
    @Input() closeIcon: string | undefined;

    /**
     * Delay in milliseconds to close the message automatically.
     */
    @Input() life: number | undefined;

    /**
     * Defines the size of the component.
     */
    @Input() size: UiMessageSize;

    /**
     * Specifies the variant of the component.
     */
    @Input() variant: UiMessageVariant;

    /**
     * Emits when the message is closed.
     */
    @Output() onClose = new EventEmitter<{ originalEvent: Event }>();

    // Keep compatibility with existing deep selectors like `.p-message.p-element`.
    @HostBinding('class') hostClass = 'p-message p-element';

    visible = true;

    private lifeTimer: ReturnType<typeof setTimeout> | undefined;

    ngOnInit(): void {
        this.scheduleAutoClose();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['life']) {
            this.scheduleAutoClose();
        }
    }

    ngOnDestroy(): void {
        this.clearLifeTimer();
    }

    get containerClass(): string {
        const severity = (this.severity ?? 'info').toString();
        const variantClass =
            this.variant === 'outlined' ? 'p-message-outlined' : this.variant === 'simple' ? 'p-message-simple' : '';
        const sizeClass = this.size === 'small' ? 'p-message-sm' : this.size === 'large' ? 'p-message-lg' : '';
        return `p-message-${severity} ${variantClass} ${sizeClass}`.trim() + (this.styleClass ? ` ${this.styleClass}` : '');
    }

    /**
     * Prime-like default icon behavior:
     * If caller did not provide an icon, show one based on severity (matches what users expect from `p-message`).
     */
    get computedIcon(): string | undefined {
        if (this.icon && this.icon.trim().length > 0) return this.icon;

        const sev = (this.severity ?? 'info').toString();
        switch (sev) {
            case 'success':
                return 'pi pi-check';
            case 'info':
                return 'pi pi-info-circle';
            case 'warn':
                return 'pi pi-exclamation-triangle';
            case 'error':
                return 'pi pi-times-circle';
            default:
                return undefined;
        }
    }

    get styleObject(): { [klass: string]: any } | null {
        return this.style && typeof this.style === 'object' ? this.style : null;
    }

    get styleString(): string | null {
        return typeof this.style === 'string' ? this.style : null;
    }

    close(event: Event): void {
        this.visible = false;
        this.clearLifeTimer();
        this.onClose.emit({ originalEvent: event });
    }

    private scheduleAutoClose(): void {
        this.clearLifeTimer();
        if (!this.life || this.life <= 0) return;
        this.lifeTimer = setTimeout(() => {
            this.close(new Event('close'));
        }, this.life);
    }

    private clearLifeTimer(): void {
        if (this.lifeTimer) {
            clearTimeout(this.lifeTimer);
            this.lifeTimer = undefined;
        }
    }
}

